import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';

const API_URL = process.env.REACT_APP_API_URL || '/api';

function formatDate(value) {
  if (!value) return '-';

  try {
    return new Date(`${value}T00:00:00`).toLocaleDateString('de-DE');
  } catch (err) {
    return value;
  }
}

function getTodayDateString() {
  return new Date().toISOString().slice(0, 10);
}

function PremiumOffer() {
  const { token } = useParams();
  const [offer, setOffer] = useState(null);
  const [requestedName, setRequestedName] = useState('');
  const [desiredStartDate, setDesiredStartDate] = useState(getTodayDateString());
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const minStartDate = useMemo(() => getTodayDateString(), []);

  useEffect(() => {
    let active = true;

    const loadOffer = async () => {
      setLoading(true);
      setError('');

      try {
        const response = await fetch(`${API_URL}/premium-offer/${encodeURIComponent(token)}`);
        const data = await response.json();

        if (!active) return;

        if (!response.ok) {
          setOffer(data.offer || null);
          throw new Error(data.error || 'Dieses Premium-Angebot ist nicht mehr verfügbar.');
        }

        setOffer(data.offer || null);
        setRequestedName(data.offer?.customer_name || '');

        if (data.offer?.status === 'accepted') {
          setSuccess(true);
        }
      } catch (err) {
        console.error(err);
        if (active) {
          setError(err.message || 'Premium-Angebot konnte nicht geladen werden.');
        }
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    };

    loadOffer();

    return () => {
      active = false;
    };
  }, [token]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');
    setSubmitting(true);

    try {
      const response = await fetch(`${API_URL}/premium-offer/${encodeURIComponent(token)}/accept`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          requested_name: requestedName,
          desired_start_date: desiredStartDate,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        if (data.offer) {
          setOffer(data.offer);
        }
        throw new Error(data.error || 'Premium-Angebot konnte nicht angenommen werden.');
      }

      setOffer(data.offer);
      setSuccess(true);
    } catch (err) {
      console.error(err);
      setError(err.message || 'Premium-Angebot konnte nicht angenommen werden.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <main className="page-shell">
        <div className="brand-header">
          <p className="eyebrow">YourGermanAddress</p>
          <h1>Premium-Testangebot</h1>
        </div>
        <div className="card">
          <p className="status-description">Angebot wird geladen...</p>
        </div>
      </main>
    );
  }

  if (error && !offer?.available && !success) {
    return (
      <main className="page-shell">
        <div className="brand-header">
          <p className="eyebrow">YourGermanAddress</p>
          <h1>Premium-Testangebot</h1>
        </div>
        <div className="card">
          <div className="hint-box">{error}</div>
        </div>
      </main>
    );
  }

  if (success || offer?.status === 'accepted') {
    return (
      <main className="page-shell">
        <div className="brand-header">
          <p className="eyebrow">YourGermanAddress</p>
          <h1>Premium-Testmonat aktiviert</h1>
        </div>
        <div className="card">
          <div className="success-box">
            Vielen Dank. Ihr kostenloser Premium-Testmonat wurde aktiviert.
          </div>
          <div className="status-grid status-grid--stack-mobile">
            <div className="status-box">
              <span>Startdatum</span>
              <strong>{formatDate(offer?.start_date)}</strong>
            </div>
            <div className="status-box">
              <span>Enddatum</span>
              <strong>{formatDate(offer?.end_date)}</strong>
            </div>
          </div>
          <p className="status-description">
            Das Angebot endet automatisch. Für ein dauerhaftes Upgrade kontaktieren Sie bitte info@yourgermanaddress.com.
          </p>
        </div>
      </main>
    );
  }

  return (
    <main className="page-shell">
      <div className="brand-header">
        <p className="eyebrow">YourGermanAddress</p>
        <h1>Kostenloser Premium-Testmonat</h1>
        <p className="subtitle">
          Aktivieren Sie Ihren kostenlosen Monat Premium / Same-Day Upload.
        </p>
      </div>

      <div className="card">
        <form onSubmit={handleSubmit}>
          <div className="form-grid">
            <label>
              Name
              <input
                type="text"
                value={requestedName}
                onChange={(event) => setRequestedName(event.target.value)}
                required
              />
            </label>
            <label>
              Gewünschtes Startdatum
              <input
                type="date"
                value={desiredStartDate}
                min={minStartDate}
                onChange={(event) => setDesiredStartDate(event.target.value)}
                required
              />
            </label>
          </div>

          {error ? <div className="hint-box" style={{ marginTop: '16px' }}>{error}</div> : null}

          <button className="primary-button" type="submit" disabled={submitting} style={{ marginTop: '18px' }}>
            {submitting ? 'Wird aktiviert...' : 'Premium-Testangebot annehmen'}
          </button>
        </form>
      </div>
    </main>
  );
}

export default PremiumOffer;
